import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

const NotificationCenter = () => {
  const authContext = useAuth();
  const user = authContext?.user;
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadNotifications();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      
      // Fetch real notifications from leave_requests table
      const { data: leaveNotifications, error: leaveError } = await supabase?.from('leave_requests')?.select('id, leave_type, start_date, end_date, status, created_at')?.eq('employee_id', user?.id)?.order('created_at', { ascending: false })?.limit(5);

      if (leaveError) throw leaveError;

      // Transform leave requests into notifications
      const transformedNotifications = leaveNotifications?.map(leave => ({
        id: leave?.id,
        type: 'leave_approval',
        title: `Leave Request ${leave?.status?.charAt(0)?.toUpperCase() + leave?.status?.slice(1)}`,
        message: `Your ${leave?.leave_type} leave request from ${new Date(leave?.start_date)?.toLocaleDateString()} to ${new Date(leave?.end_date)?.toLocaleDateString()} has been ${leave?.status}.`,
        timestamp: new Date(leave?.created_at),
        read: false,
        priority: leave?.status === 'approved' ? 'high' : 'medium'
      })) || [];

      setNotifications(transformedNotifications);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev?.map(notif => 
        notif?.id === notificationId 
          ? { ...notif, read: true }
          : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev?.map(notif => ({ ...notif, read: true }))
    );
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'leave_approval':
        return 'Calendar';
      case 'payroll':
        return 'DollarSign';
      case 'profile_update':
        return 'User';
      case 'system':
        return 'Settings';
      case 'benefits':
        return 'Shield';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'high') return 'var(--color-error)';
    if (priority === 'medium') return 'var(--color-warning)';
    
    switch (type) {
      case 'leave_approval':
        return 'var(--color-success)';
      case 'payroll':
        return 'var(--color-primary)';
      case 'profile_update':
        return 'var(--color-accent)';
      case 'system':
        return 'var(--color-secondary)';
      case 'benefits':
        return 'var(--color-primary)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border card-shadow p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border card-shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-1 bg-error text-error-foreground text-xs rounded-full font-medium">
              {unreadCount}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllAsRead}>
              Mark all read
            </Button>
          )}
          <Button variant="ghost" size="sm">
            <Icon name="Settings" size={16} />
          </Button>
        </div>
      </div>
      
      {notifications?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Bell" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground">No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {notifications?.map((notification) => (
            <div
              key={notification?.id}
              onClick={() => !notification?.read && markAsRead(notification?.id)}
              className={`
                p-4 rounded-lg border cursor-pointer transition-smooth
                ${
                  notification?.read
                    ? 'bg-card border-border opacity-70' :'bg-accent/5 border-accent/20 hover:bg-accent/10'
                }
              `}
            >
              <div className="flex items-start space-x-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${getNotificationColor(notification?.type, notification?.priority)}15` }}
                >
                  <Icon 
                    name={getNotificationIcon(notification?.type)} 
                    size={18} 
                    color={getNotificationColor(notification?.type, notification?.priority)}
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-medium text-foreground text-sm">
                      {notification?.title}
                    </h4>
                    <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                      {formatTimestamp(notification?.timestamp)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {notification?.message}
                  </p>
                  
                  {!notification?.read && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        New
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;